import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { all_routes } from "../../router/all_routes";
import { API_URL } from "../../../environment";
import { canViewLeadsType } from "./access";

// ── Tipos ───────────────────────────────────────────────────────────────────
interface CardCrm {
  id: number;
  fone: string;
  etapa?: string | null;
  atendente?: string | null;
  criado_em?: string | null;
}

interface LeadType {
  fone: string;
  nome?: string | null;
  created_at?: string | null;
  status: string;
  qualificado: boolean;
  origem?: string | null;
  sessoes: number;
  id_time?: number | null;
  time?: string | null;
  card: CardCrm | null;
  motivo?: string | null;
}

// "auxilio-acidente-chat-nm" -> "Auxílio-acidente (chat)". Slug desconhecido volta como veio.
const NOMES_ORIGEM: Record<string, string> = {
  "auxilio-acidente": "Auxílio-acidente",
  "emprestimo-conta-corrente": "Empréstimo conta corrente",
  "dividas-empresariais": "Dívidas empresariais",
};
const nomeOrigem = (o?: string | null): string => {
  if (!o) return "—";
  const m = o.match(/^(simulador-)?(.+?)(-chat)?(-(nm|br))?$/);
  if (!m) return o;
  const base = NOMES_ORIGEM[m[2]] || m[2];
  if (m[1]) return `${base} (simulador)`;
  if (m[3]) return `${base} (chat)`;
  return base;
};

// "5541998561270" -> "(41) 99856-1270"
const formatFone = (f: string): string => {
  const d = (f || "").replace(/\D/g, "").replace(/^55(?=\d{10,11}$)/, "");
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return f;
};

const formatData = (iso?: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
};

// O card mais recente do fone é de ANTES do lead do type: o contato já estava no CRM
// (outra origem); o type não criou card novo.
const cardJaExistia = (l: LeadType): boolean =>
  !!(l.card?.criado_em && l.created_at && new Date(l.card.criado_em).getTime() < new Date(l.created_at).getTime() - 60_000);

const statusCor = (s: string) =>
  s === "Qualificado" ? "#16a34a" : s === "Desqualificado" ? "#dc2626" : "#d97706";

// Página "Leads do Type" — conferência, somente visualização. Cada linha é um telefone que
// passou pelo funil do type (type.advescala / type.conversaicrm): status lá e, ao lado, o
// card no CRM (etapa atual, atendente) ou o motivo de não ter entrado.
const LeadsType = () => {
  const navigate = useNavigate();
  const autorizado = canViewLeadsType();
  const isDark = useSelector((state: any) => state.themeSetting?.dataTheme) === "dark";
  const idGrupo = localStorage.getItem("id_grupo") || "";
  const idTime = localStorage.getItem("id_cliente") || "";
  const adminMaster = localStorage.getItem("isAdminMaster") === "true";

  const [leads, setLeads] = useState<LeadType[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  // ── Filtros ─────────────────────────────────────────────────────────────
  const [dias, setDias] = useState<string>("30");
  const [filtroStatus, setFiltroStatus] = useState<string>("Qualificado");
  const [filtroCrm, setFiltroCrm] = useState<string>("");
  const [filtroTime, setFiltroTime] = useState<string>("");
  const [busca, setBusca] = useState<string>("");

  // Bloqueio de rota: quem não tem acesso volta para o chat, mesmo via URL direta.
  useEffect(() => {
    if (!autorizado) navigate(all_routes.chat, { replace: true });
  }, [autorizado, navigate]);

  useEffect(() => {
    if (!autorizado) return;
    let abort = false;
    (async () => {
      setCarregando(true);
      setErro(null);
      try {
        const q = new URLSearchParams({ id_grupo: idGrupo, id_time: idTime, admin_master: String(adminMaster), dias });
        const resp = await fetch(`${API_URL}/leads_type?${q.toString()}`);
        const data = await resp.json().catch(() => ({}));
        if (abort) return;
        if (!resp.ok) {
          setErro(data?.error || "Não foi possível carregar os leads do type.");
          setLeads([]);
        } else {
          setLeads(Array.isArray(data?.leads) ? data.leads : []);
        }
      } catch (e) {
        if (!abort) setErro("Não foi possível carregar os leads do type.");
      } finally {
        if (!abort) setCarregando(false);
      }
    })();
    return () => {
      abort = true;
    };
  }, [autorizado, idGrupo, idTime, adminMaster, dias]);

  const times = useMemo(() => {
    const m = new Map<string, string>();
    leads.forEach((l) => l.id_time && m.set(String(l.id_time), l.time || `Time ${l.id_time}`));
    return Array.from(m.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [leads]);

  const filtrados = useMemo(() => {
    const b = busca.trim().toLowerCase();
    const bDig = b.replace(/\D/g, "");
    return leads.filter((l) => {
      if (filtroStatus && l.status !== filtroStatus) return false;
      if (filtroCrm === "no" && !l.card) return false;
      if (filtroCrm === "fora" && l.card) return false;
      if (filtroTime && String(l.id_time) !== filtroTime) return false;
      if (b && !((l.nome || "").toLowerCase().includes(b) || (bDig && l.fone.includes(bDig)))) return false;
      return true;
    });
  }, [leads, filtroStatus, filtroCrm, filtroTime, busca]);

  // Totais do recorte de período/time (os de status e CRM são os próprios números exibidos).
  const resumo = useMemo(() => {
    const base = leads.filter((l) => !filtroTime || String(l.id_time) === filtroTime);
    const qual = base.filter((l) => l.qualificado);
    const noCrm = qual.filter((l) => l.card);
    return {
      qualificados: qual.length,
      noCrm: noCrm.length,
      fora: qual.length - noCrm.length,
      jaExistiam: noCrm.filter(cardJaExistia).length,
    };
  }, [leads, filtroTime]);

  const filtrosAtivos = filtroStatus !== "Qualificado" || !!filtroCrm || !!filtroTime || !!busca;
  const limparFiltros = () => {
    setFiltroStatus("Qualificado");
    setFiltroCrm("");
    setFiltroTime("");
    setBusca("");
  };

  const baixarCsv = () => {
    const cab = ["Data do lead", "Nome", "Telefone", "Funil do type", "Status no type", "No CRM", "Etapa atual", "Atendente", "Time", "Card criado em", "Observação"];
    const linhas = filtrados.map((l) => [
      formatData(l.created_at), l.nome || "", formatFone(l.fone), nomeOrigem(l.origem), l.status,
      l.card ? "Sim" : "Não", l.card?.etapa || "", l.card?.atendente || "", l.time || "",
      l.card ? formatData(l.card.criado_em) : "",
      l.card ? (cardJaExistia(l) ? "Contato já existia no CRM antes do type" : "") : l.motivo || "",
    ]);
    const esc = (v: string) => `"${String(v).replace(/"/g, '""')}"`;
    const csv = "﻿" + [cab, ...linhas].map((r) => r.map(esc).join(";")).join("\r\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `leads-type-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!autorizado) return null;

  // ── Cores (mesmas da tela Templates com Erro) ─────────────────────────────
  const bgCard = isDark ? "#1f2c34" : "#ffffff";
  const bgHead = isDark ? "#172229" : "#f8fafc";
  const border = isDark ? "#2a3942" : "#e6e9ef";
  const textMain = isDark ? "#e9edef" : "#1a1a2e";
  const textMuted = isDark ? "#8696a0" : "#6b7280";
  const lbl: React.CSSProperties = { fontSize: 11, color: textMuted, fontWeight: 600 };
  const th: React.CSSProperties = {
    fontSize: 11, fontWeight: 700, color: textMuted, textTransform: "uppercase", letterSpacing: 0.3,
    padding: "10px 12px", whiteSpace: "nowrap", background: bgHead, borderBottom: `1px solid ${border}`,
    position: "sticky", top: 0, zIndex: 1,
  };
  const td: React.CSSProperties = { fontSize: 13, color: textMain, padding: "10px 12px", borderBottom: `1px solid ${border}`, verticalAlign: "top" };

  const Kpi = ({ titulo, valor, cor, dica }: { titulo: string; valor: number; cor: string; dica: string }) => (
    <div
      title={dica}
      style={{ flex: "1 1 160px", background: bgCard, border: `1px solid ${border}`, borderRadius: 10, padding: "12px 14px", borderTop: `3px solid ${cor}` }}
    >
      <div style={{ fontSize: 12, color: textMuted, fontWeight: 600 }}>{titulo}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: textMain, lineHeight: 1.2 }}>{carregando ? "…" : valor}</div>
    </div>
  );

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Um bloco só: o .content é flex em coluna com wrap e, no celular, mandava a lista para uma 2ª coluna fora da tela */}
        <div style={{ width: "100%" }}>
        {/* Cabeçalho */}
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <div>
            <h4 className="mb-1" style={{ color: textMain }}>
              <i className="ti ti-filter-check me-2" style={{ color: "#16a34a" }} />
              Leads do Type
            </h4>
            <p className="mb-0" style={{ color: textMuted, fontSize: 13 }}>
              Quem passou pelo funil do type e onde está no CRM. Um telefone por linha. Somente visualização.
            </p>
          </div>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={baixarCsv} disabled={!filtrados.length}>
            <i className="ti ti-download me-1" />
            Baixar planilha (CSV)
          </button>
        </div>

        {/* Totais */}
        <div className="d-flex flex-wrap gap-2 mb-3">
          <Kpi titulo="Qualificados no type" valor={resumo.qualificados} cor="#16a34a" dica="Telefones com status Qualificado no type, no período" />
          <Kpi titulo="Qualificados no CRM" valor={resumo.noCrm} cor="#2563eb" dica="Qualificados que têm card no CRM" />
          <Kpi titulo="Qualificados fora do CRM" valor={resumo.fora} cor="#dc2626" dica="Qualificados sem card no CRM (veja o motivo na lista)" />
          <Kpi titulo="Já estavam no CRM" valor={resumo.jaExistiam} cor="#9333ea" dica="O contato já tinha card no CRM antes de passar pelo type" />
        </div>

        {/* Filtros */}
        <div
          className="d-flex align-items-end flex-wrap gap-2 mb-3 p-2"
          style={{ background: bgCard, border: `1px solid ${border}`, borderRadius: 8 }}
        >
          <div style={{ minWidth: 150 }}>
            <label style={lbl}>Período</label>
            <select className="form-select form-select-sm" value={dias} onChange={(e) => setDias(e.target.value)}>
              <option value="7">Últimos 7 dias</option>
              <option value="15">Últimos 15 dias</option>
              <option value="30">Últimos 30 dias</option>
              <option value="90">Últimos 90 dias</option>
              <option value="0">Tudo</option>
            </select>
          </div>
          <div style={{ minWidth: 150 }}>
            <label style={lbl}>Status no type</label>
            <select className="form-select form-select-sm" value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)}>
              <option value="">Todos</option>
              <option value="Qualificado">Qualificados</option>
              <option value="Desqualificado">Desqualificados</option>
              <option value="incompleto">Incompletos</option>
            </select>
          </div>
          <div style={{ minWidth: 150 }}>
            <label style={lbl}>No CRM</label>
            <select className="form-select form-select-sm" value={filtroCrm} onChange={(e) => setFiltroCrm(e.target.value)}>
              <option value="">Todos</option>
              <option value="no">Com card no CRM</option>
              <option value="fora">Fora do CRM</option>
            </select>
          </div>
          {adminMaster && times.length > 1 && (
            <div style={{ minWidth: 170 }}>
              <label style={lbl}>Time</label>
              <select className="form-select form-select-sm" value={filtroTime} onChange={(e) => setFiltroTime(e.target.value)}>
                <option value="">Todos os times</option>
                {times.map(([id, nome]) => (
                  <option key={id} value={id}>{nome}</option>
                ))}
              </select>
            </div>
          )}
          <div style={{ minWidth: 200, flex: 1 }}>
            <label style={lbl}>Buscar</label>
            <input
              className="form-control form-control-sm"
              placeholder="Nome ou telefone"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
          </div>
          {filtrosAtivos && (
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={limparFiltros}>
              <i className="ti ti-x me-1" />
              Limpar
            </button>
          )}
        </div>

        {/* Lista */}
        <div style={{ background: bgCard, border: `1px solid ${border}`, borderRadius: 10, overflow: "auto", maxHeight: "calc(100vh - 330px)", minHeight: 200 }}>
          {carregando ? (
            <div className="p-4 text-center" style={{ color: textMuted }}>Carregando…</div>
          ) : erro ? (
            <div className="p-4 text-center" style={{ color: "#dc2626" }}>{erro}</div>
          ) : !filtrados.length ? (
            <div className="p-4 text-center" style={{ color: textMuted }}>Nenhum lead com esses filtros.</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
              <thead>
                <tr>
                  <th style={th}>Data do lead</th>
                  <th style={th}>Nome / telefone</th>
                  <th style={th}>Funil do type</th>
                  <th style={th}>Status no type</th>
                  <th style={th}>No CRM</th>
                  <th style={th}>Etapa atual</th>
                  <th style={th}>Atendente</th>
                  {adminMaster && times.length > 1 && <th style={th}>Time</th>}
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {filtrados.map((l) => {
                  const jaExistia = cardJaExistia(l);
                  return (
                    <tr key={l.fone}>
                      <td style={{ ...td, whiteSpace: "nowrap" }}>
                        {formatData(l.created_at)}
                        {l.sessoes > 1 && (
                          <div style={{ fontSize: 11, color: textMuted }} title="Vezes que o telefone passou pelo funil do type no período">
                            {l.sessoes} passagens
                          </div>
                        )}
                      </td>
                      <td style={td}>
                        <div style={{ fontWeight: 600 }}>{l.nome || "—"}</div>
                        <div style={{ fontSize: 12, color: textMuted, whiteSpace: "nowrap" }}>{formatFone(l.fone)}</div>
                      </td>
                      <td style={td}>{nomeOrigem(l.origem)}</td>
                      <td style={td}>
                        <span style={{ color: statusCor(l.status), fontWeight: 600, whiteSpace: "nowrap" }}>
                          {l.status === "incompleto" ? "Incompleto" : l.status}
                        </span>
                      </td>
                      <td style={{ ...td, maxWidth: 260 }}>
                        {l.card ? (
                          <>
                            <span style={{ color: "#16a34a", fontWeight: 600, whiteSpace: "nowrap" }}>
                              <i className="ti ti-circle-check me-1" />
                              Sim
                            </span>
                            {jaExistia && (
                              <div style={{ fontSize: 11, color: "#9333ea" }} title={`Card criado em ${formatData(l.card.criado_em)}`}>
                                já estava no CRM antes do type
                              </div>
                            )}
                          </>
                        ) : l.qualificado ? (
                          <>
                            <span style={{ color: "#dc2626", fontWeight: 600, whiteSpace: "nowrap" }}>
                              <i className="ti ti-circle-x me-1" />
                              Não
                            </span>
                            {l.motivo && <div style={{ fontSize: 11, color: textMuted }}>{l.motivo}</div>}
                          </>
                        ) : (
                          <span style={{ color: textMuted, fontSize: 12 }} title="Só os qualificados vão para o CRM">
                            Não vai (não qualificado)
                          </span>
                        )}
                      </td>
                      <td style={td}>{l.card?.etapa || "—"}</td>
                      <td style={td}>{l.card?.atendente?.trim() || "—"}</td>
                      {adminMaster && times.length > 1 && <td style={td}>{l.time || "—"}</td>}
                      <td style={{ ...td, whiteSpace: "nowrap" }}>
                        {l.card && (
                          <a
                            href={`${all_routes.chat}?fone=${encodeURIComponent(l.card.fone)}${l.id_time ? `&id_time=${l.id_time}` : ""}`}
                            className="btn btn-sm btn-outline-primary"
                            title="Abrir a conversa no chat"
                          >
                            <i className="ti ti-message-circle" />
                          </a>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        {!carregando && !erro && (
          <div className="mt-2" style={{ fontSize: 12, color: textMuted }}>
            {filtrados.length} de {leads.length} telefone(s) no período.
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default LeadsType;
